import { useEffect } from 'react';
import { useFilingCabinetContext } from '../../../Contexts/Authenticated/FilingCabinetContext';
import useFilingCabinetKeyInCurrentURL from '../../../Hooks/UseFilingCabinetKeyInCurrentUrl';
import FilingCabinetRazorView from '../../../Pages/RazorView/FilingCabinetRazorView';
import NonFilingCabinetRazorView from '../../../Pages/RazorView/NonFilingCabinetRazorView';
import RazorView from '../../../Pages/RazorView/RazorView';

export interface RazorRouteProps {
    razorUrl?: string;
    usesFilingCabinet?: boolean;
    reUseFilingCabinet?: boolean;
}

const createUrlWithSearchParams = (base: string, params: URLSearchParams) => {
    if (params.size === 0) {
        return base;
    }

    return `${base}?${params}`;
};

const RazorRoute = ({
    razorUrl,
    usesFilingCabinet,
    reUseFilingCabinet,
}: RazorRouteProps): React.ReactElement => {
    const { generateNewID, loadByPath } = useFilingCabinetContext();
    const [filingCabinetKey, setFilingCabinetKey, urlWithoutParams, searchParamsWithoutFilingCabinetKey] = useFilingCabinetKeyInCurrentURL();
    const path = createUrlWithSearchParams(urlWithoutParams, searchParamsWithoutFilingCabinetKey);
    const dataPath = createUrlWithSearchParams(razorUrl ?? urlWithoutParams, searchParamsWithoutFilingCabinetKey);
    const shouldUseFileCabinet = usesFilingCabinet ?? true;

    const getFilingCabinetReUseKey = (): number | undefined => {
        if (!reUseFilingCabinet) {
            return undefined;
        }

        const existingFCItemWithSamePath = loadByPath(path);
        return existingFCItemWithSamePath?.ID;
    };

    useEffect(() => {
        // Ignore if not using filing cabinet
        if (!shouldUseFileCabinet) {
            return;
        }

        // Try to find existing filingcabinet item if reuse is enabled
        const filingCabinetKeyToReUse = getFilingCabinetReUseKey();
        const alreadyHasAFilingCabinetKey = filingCabinetKey != null;

        if (filingCabinetKeyToReUse) {
            setFilingCabinetKey(filingCabinetKeyToReUse);
        }
        else if (!alreadyHasAFilingCabinetKey) {
            const newKey = generateNewID();
            setFilingCabinetKey(newKey);
        }
    }, [shouldUseFileCabinet, reUseFilingCabinet, filingCabinetKey]);

    if (shouldUseFileCabinet && filingCabinetKey) {
        return <FilingCabinetRazorView key={filingCabinetKey} filingCabinetKey={filingCabinetKey} dataPath={dataPath} path={path} />;
    }

    if (!shouldUseFileCabinet) {
        return <NonFilingCabinetRazorView dataUrl={dataPath} />;
    }

    return <RazorView />;
};

export default RazorRoute;
